import { map, Observable, ObservableInput, of, switchMap } from 'rxjs';

type ExecFn<T = boolean | null, O = unknown> = (
  value: T,
  index: number,
) => ObservableInput<O> | void;

/**
 * answer flow for portal, switch to confirm or cancel,
 * alway switch to original result value when complete confirm or cancel exec
 *
 * @example
 * ```
 * obs$
 * .pipe(
 *   answer({
 *     confirm: () =>
 *       of('confirm').pipe(
 *         tap(() => {
 *           // when confirm will auto switch to another flow
 *         }),
 *       ),
 *     cancel: () => {
 *       // also can do some thing without observable
 *     },
 *   }),
 *   tap((x) => {
 *     console.log(x); // x will be that original window confirm
 *   })
 * )
 * .subscribe();
 * ```
 */
export const answer = <T, F>({
  confirm,
  cancel,
}:
  | {
      confirm?: ExecFn<T, F>;
      cancel: ExecFn<T, F>;
    }
  | {
      confirm: ExecFn<T, F>;
      cancel?: ExecFn<T, F>;
    }) =>
  switchMap<T, ObservableInput<F | T>>((data, index) => {
    const return$ = of(data);
    const result = data ? confirm?.(data, index) : cancel?.(data, index);

    return result instanceof Observable
      ? (result as Observable<unknown>).pipe(map(() => data))
      : return$;
  });
